import * as React from "react";
import {useEffect, useRef, useState} from "react";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import {useNavigate} from "react-router";
import { Organization } from "../../models/Organization";
import OrganizationsRepository from "../../repositories/OrganizationsRepository";
import UsersRepository from "../../repositories/UsersRepository";
import {User, UserRole} from "../../models/User";
import SortableTableColumnHeader from "../common/SortableTableColumnHeader";
import GenericAutocomplete from "../common/GenericAutocomplete";
import {useAuth} from "../../contexts/AuthContext";
import SecretViewer from "../common/SecretViewer";
import TableEmptyState from "../common/TableEmptyState";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { Trash2 } from "lucide-react";
import { FilterCombobox } from "../common/FilterCombobox";
import {convertUtcToLocal} from "../../helpers/CommonHelpers";
import {QUERY_CONFIG} from "../../constants/QueryConfig";
import {QUERY_KEYS} from "../../constants/QueryKeys";
import {useTableHandlers} from "../common/Table/Hooks/UseTableHandlers";
import {
  fileFiltersBoxClassName,
  filterComboboxClassName,
  tableWrapperClassName,
} from "../common/Table/TableStyles";
import { SearchInput } from "../common/Table/SearchInput";
import TablePagination from "../common/Table/TablePagination";

interface OrganizationsTableProps {
  onDeleteOrganization: (organizationId: string, organizationName: string) => void;
  userRole: UserRole;
  userId: string;
}

export const OrganizationsTable: React.FC<OrganizationsTableProps> = ({
  onDeleteOrganization,
  userRole,
  userId
}: OrganizationsTableProps) => {
  const { getUserId } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const usersRepository = new UsersRepository();
  const organizationsRepository = new OrganizationsRepository();
  const [selectedUserId, setSelectedUserId] = React.useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [users, setUsers] = useState<User[]>([]);

  const {
    order,
    page,
    rowsPerPage,
    rows,
    sort,
    setPage,
    setRows,
    setSort,
    changeOrder,
    handleRowsPerPageChange
  } = useTableHandlers({
    order: "desc",
    sort: "dateCreated",
    page: 0,
    rowsPerPage: 50
  });

  const organizationsQuery = useQuery({ queryKey: [
      QUERY_KEYS.ORGANIZATIONS,
      selectedUserId,
      searchQuery,
      page,
      rowsPerPage,
      sort,
      order,
    ], queryFn:
    async () => await organizationsRepository.getOrganizations({
      assignedUserId: selectedUserId ?? undefined,
      searchPhrase: searchQuery,
      descending: order === "desc",
      orderBy: sort,
      pageNumber: page + 1,
      pageSize: rowsPerPage,
    }), staleTime: QUERY_CONFIG.DEFAULT_STALE_TIME,
      refetchInterval: QUERY_CONFIG.DEFAULT_REFETCH_INTERVAL,
      refetchIntervalInBackground: true,
      refetchOnMount: true, });

  const usersQuery = useQuery({ queryKey: [QUERY_KEYS.ASSIGNABLE_USERS], queryFn:
    async () => await usersRepository.getUsers({
      roles: [UserRole.Admin, UserRole.Editor],
      fetchAll: true,
      descending: false,
      orderBy: "username",
      onlyWithOrganizations: true,
    }), staleTime: QUERY_CONFIG.DEFAULT_STALE_TIME,
      refetchInterval: QUERY_CONFIG.DEFAULT_REFETCH_INTERVAL,
      refetchIntervalInBackground: true,
      refetchOnMount: false, });

  const hasAutoSelectedRef = useRef(false);

  useEffect(() => {
    if (hasAutoSelectedRef.current) return;
    if (userRole === UserRole.Viewer) return;
    if (!usersQuery.data) return;

    hasAutoSelectedRef.current = true;
    const currentUserId = getUserId();
    if (usersQuery.data.data?.some((u) => u.id === currentUserId)) {
      setSelectedUserId(currentUserId);
    }
  }, [usersQuery.data, userRole]);

  useEffect(() => {
    if (organizationsQuery.data?.data){
      setOrganizations(organizationsQuery.data.data);
      setRows(organizationsQuery.data.paging?.totalCount ?? 0);
    }
  }, [organizationsQuery.data]);

  useEffect(() => {
    if (usersQuery.data?.data)
      setUsers(usersQuery.data.data);
  }, [usersQuery.data]);

  const userOptions = users.map(user => ({
    id: user.id,
    label: user.username,
  }));

  const handleUserChange = (newValue: string | null) =>
    setSelectedUserId(newValue);

  const handleSearch = (organizationName: string) =>
    setSearchQuery(organizationName ?? null);

  return (
    <div className="flex flex-col w-full flex-1 min-h-0">
      <div className={fileFiltersBoxClassName}>
        <SearchInput
          label="Search organizations"
          placeholder="Organization name"
          onSearch={(value) => handleSearch(value)}
          className={cn((usersQuery.isFetching || usersQuery.isError) && "opacity-50 pointer-events-none")}
        />
        <div className={cn(filterComboboxClassName, "w-[260px]", (usersQuery.isFetching || usersQuery.isError) && "opacity-50 pointer-events-none")}>
          <Label className="text-xs font-semibold text-muted-foreground">Assigned to</Label>
            <FilterCombobox
              value={selectedUserId}
              onChange={handleUserChange}
              placeholder="Any user"
              searchable
              options={userOptions.map((o) => ({ value: o.id, label: o.label }))}
            />
        </div>
      </div>
      <div className={cn(tableWrapperClassName, (organizationsQuery.isFetching || organizationsQuery.isError) && "opacity-50 pointer-events-none")}>
          <Table containerClassName="flex-1 min-h-0">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[12%] px-1.5 py-3">
                  <SortableTableColumnHeader
                    label={"Intact ID"}
                    sortProperty={"intactId"}
                    getSort={() => sort}
                    setSort={(newSort) => setSort(newSort)}
                    getOrder={() => order}
                    setOrder={(newOrder) => changeOrder(newOrder)}
                  />
                </TableHead>
                <TableHead className="w-[25%] px-1.5 py-3">
                  <SortableTableColumnHeader
                    label={"Name"}
                    sortProperty={"name"}
                    getSort={() => sort}
                    setSort={(newSort) => setSort(newSort)}
                    getOrder={() => order}
                    setOrder={(newOrder) => changeOrder(newOrder)}
                  />
                </TableHead>
                <TableHead className="w-[28%] px-1.5 py-3">
                  Secret
                </TableHead>
                <TableHead className="w-[15%] px-1.5 py-3">
                  <SortableTableColumnHeader
                    label={"Date Created"}
                    sortProperty={"dateCreated"}
                    getSort={() => sort}
                    setSort={(newSort) => setSort(newSort)}
                    getOrder={() => order}
                    setOrder={(newOrder) => changeOrder(newOrder)}
                  />
                </TableHead>
                <TableHead className="w-[20%] px-1.5 py-3">
                  Assigned to
                </TableHead>
                <TableHead className="w-[60px]"/>
              </TableRow>
            </TableHeader>
            <TableBody>
              {organizations.map((organization) => {
                let assignedUser = organization.assignedUser
                  ? { id: organization.assignedUser.id, label: organization.assignedUser.username }
                  : { id: '', label: '' };

                const handleSelectUser = async (selectedOption: { id: string; label: string }, organizationId: string) => {
                  try {
                    await organizationsRepository.patchOrganizationAssignedUser(organizationId, selectedOption.id);
                    assignedUser.id = selectedOption.id;
                    assignedUser.label = selectedOption.label;
                    await Promise.all([
                      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORGANIZATIONS] }),
                      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORGANIZATION, organizationId] }),
                      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORGANIZATION_NAVIGATION_INFO, organizationId] }),
                      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ASSIGNABLE_USERS] }),
                      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.USERS] }),
                    ]);
                    toast.success(
                      selectedOption.id
                        ? `Assigned ${selectedOption.label} to ${organization.name}`
                        : `Unassigned user from ${organization.name}`,
                    );
                  } catch (error) {
                    toast.error(
                      error instanceof Error
                        ? error.message
                        : "Failed to update assigned user",
                    );
                    return;
                  }
                };

                return (
                  <TableRow
                    key={organization.id}
                    className="cursor-pointer"
                    tabIndex={0}
                    onClick={() =>
                      navigate(`/organization/${organization.id}`)
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        navigate(`/organization/${organization.id}`);
                      }
                    }}
                  >
                    <TableCell>
                      <span className="text-sm break-all">
                        {organization.intactId}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm break-words">
                        {organization.name}
                      </span>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      {userRole === UserRole.Viewer
                        ? <span className="text-sm text-muted-foreground">—</span>
                        : <SecretViewer organization={organization}/>}
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">
                        {convertUtcToLocal(organization.dateCreated as string)}
                      </span>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <GenericAutocomplete
                        disabled={
                          userRole === UserRole.Viewer
                          || (assignedUser.id != userId && userRole !== UserRole.Admin)
                        }
                        options={userOptions}
                        inputValue={assignedUser}
                        setValue={(selectedOption) => handleSelectUser(selectedOption, organization.id)}
                        placeholder="Assigned user"
                      />
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      {/* Deleting an organization stops its clients for good, so only an Admin may. */}
                      <Button
                        disabled={userRole !== UserRole.Admin}
                        aria-label="Delete"
                        variant="ghost"
                        size="icon-sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => onDeleteOrganization(organization.id ?? "", organization.name ?? "")}
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
              {organizations.length === 0 && !organizationsQuery.isFetching && (
                <TableRow className="hover:bg-transparent">
                  <TableCell colSpan={99}>
                    <TableEmptyState title="No organizations found" />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            page={page}
            rowsPerPage={rowsPerPage}
            rows={rows}
            onPageChange={setPage}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
      </div>
    </div>
  );
};
